import ThemeModal from "components/Common/ThemeModal"
import { Badge, Button, Col, Row, Spinner } from "reactstrap"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { formatReadableDate } from "utils/formatDate"

const STATUS_COLORS = {
  pending: "warning",
  completed: "success",
  cancelled: "secondary",
}

const formatDutyAmount = amount => {
  return `Tk. ${new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount || 0))}`
}

const DetailRow = ({ label, value, mono = false, strong = false }) => (
  <div
    className="d-flex align-items-start justify-content-between gap-3 py-2"
    style={{ borderBottom: "1px dashed rgba(15, 23, 42, 0.12)" }}
  >
    <span
      className="text-muted text-uppercase fw-semibold"
      style={{ fontSize: 10, letterSpacing: 1 }}
    >
      {label}
    </span>
    <span
      className={`${mono ? "font-monospace" : ""} ${strong ? "fw-bold" : "fw-semibold"} text-dark text-end`}
      style={{ fontSize: strong ? 15 : 13 }}
    >
      {value || "-"}
    </span>
  </div>
)

const Section = ({ title, children }) => (
  <div className="pt-3">
    <div
      className="text-uppercase text-muted fw-semibold mb-2"
      style={{ fontSize: 11, letterSpacing: 1 }}
    >
      {title}
    </div>
    {children}
  </div>
)

const AccountDutyPaymentView = ({
  modal,
  toggleModal,
  dutyPayment,
  loading,
  onComplete,
  onPrint,
  printLoadingId,
}) => {
  const item = dutyPayment

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title="Duty Payment Details"
      size="xl"
      modalBodyClass="bg-body-tertiary"
    >
      {loading ? (
        <div className="text-center py-4">
          <Spinner size="sm" />
        </div>
      ) : !item ? (
        <div className="text-center py-4 text-muted">No details available.</div>
      ) : (
        <div className="mx-auto" style={{ maxWidth: 820 }}>
          <div
            className="rounded-4 border overflow-hidden bg-white"
            style={{
              borderColor: "rgba(15, 23, 42, 0.08)",
              boxShadow: "0 24px 64px rgba(15, 23, 42, 0.08)",
            }}
          >
            <div
              className="px-4 py-2 d-flex align-items-center justify-content-between"
              style={{ background: "linear-gradient(90deg, #182033 0%, #243145 100%)" }}
            >
              <div className="text-white text-uppercase fw-semibold" style={{ fontSize: 10, letterSpacing: 1.6 }}>
                Customs Duty Payment
              </div>
              <div className="d-flex align-items-center gap-2">
                <Badge color={STATUS_COLORS[item.status] || "secondary"} pill>
                  {item.status || "pending"}
                </Badge>
                <span className="text-white-50 font-monospace" style={{ fontSize: 12 }}>
                  {item.duty_payment_no}
                </span>
              </div>
            </div>

            <div className="px-4 py-3 border-bottom" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
                <div>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>
                    Vehicle
                  </div>
                  <div className="h5 mb-1 text-dark font-monospace">{item.chassis_no}</div>
                  <div className="text-muted small">
                    Bill of Entry: {item.bill_of_entry_registration_no || "-"}
                  </div>
                </div>
                <div className="text-md-end">
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>
                    Duty Amount
                  </div>
                  <div className="fw-bold text-dark" style={{ fontSize: 32, lineHeight: 1 }}>
                    {formatDutyAmount(item.amount)}
                  </div>
                  {item.amount_in_words && (
                    <div className="text-muted small mt-1">{item.amount_in_words}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <Row className="g-3 g-md-4">
                <Col md={6}>
                  <Section title="Bank Letter">
                    <DetailRow label="Date" value={item.date ? formatReadableDate(item.date) : "-"} />
                    <DetailRow label="Designation" value={item.designation} />
                    <DetailRow label="Bank" value={item.bank_name} />
                    <DetailRow label="Branch" value={item.branch_name} />
                    <DetailRow label="Account Name" value={item.account_name} />
                    <DetailRow label="Account Number" value={item.account_number} mono />
                  </Section>
                </Col>
                <Col md={6}>
                  <Section title="Bill of Entry">
                    <DetailRow label="Chassis No" value={item.chassis_no} mono />
                    <DetailRow label="Model Year" value={item.model_year} />
                    <DetailRow label="Registration No" value={item.bill_of_entry_registration_no} mono />
                    <DetailRow label="Registration Year" value={item.registration_year} />
                    <DetailRow label="Declarant Code (AIN)" value={item.declarant_code_ain} mono />
                    <DetailRow label="Customs Office Code" value={item.customs_office_code} mono />
                  </Section>
                </Col>
              </Row>
            </div>

            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <Section title="Ledger and Status">
                <DetailRow label="Duty Payment No" value={item.duty_payment_no} mono strong />
                <DetailRow label="Status" value={item.status} />
                <DetailRow label="Debit Ledger Account" value={item.account?.name || (item.account_id ? `Account #${item.account_id}` : "-")} />
                <DetailRow label="Company Bank Account" value={item.company_bank_account?.label || item.companyBankAccount?.label} />
              </Section>
            </div>

            {item.notes && (
              <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
                <Section title="Notes">
                  <div className="rounded-3 px-3 py-2" style={{ background: "#f8fafc", whiteSpace: "pre-wrap" }}>
                    {item.notes}
                  </div>
                </Section>
              </div>
            )}

            <div className="px-4 py-3 bg-light">
              <div className="d-flex flex-wrap gap-2 align-items-center justify-content-end">
                <Button
                  color="info"
                  outline
                  size="sm"
                  onClick={() => onPrint?.(item.id)}
                  disabled={printLoadingId === item.id}
                >
                  <i className="mdi mdi-file-pdf-box me-1" />
                  Download Letter
                </Button>
                <CanView access={[ACCESS.ACCOUNT_DUTY_PAYMENT.COMPLETE]}>
                  {item.status === "pending" && (
                    <Button color="success" size="sm" onClick={() => onComplete?.(item.id)}>
                      <i className="mdi mdi-check-circle-outline me-1" />
                      Mark Complete
                    </Button>
                  )}
                </CanView>
              </div>
            </div>
          </div>
        </div>
      )}
    </ThemeModal>
  )
}

export default AccountDutyPaymentView
